import React from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { OfferRecord } from '../../api/OfferPortalApi';
import {
  formatOfferDate,
  offerConveyanceLabel,
  OFFER_SOURCE_LABELS,
  OFFER_TYPE_LABELS,
  offerTermsSummary,
} from './offerDisplay';

/**
 * A recorded offer, read-only. This is what the "Offer received" box on the case journey expands
 * into (Lloyd, Aug 18), and what the Offer tab lists — one description of an offer, used twice.
 */
interface OfferSummaryProps {
  offer: OfferRecord;
  /** Set on anything older than the newest offer on the case, so the current one is unambiguous. */
  superseded?: boolean;
}

function OfferSummary({ offer, superseded = false }: OfferSummaryProps) {
  const terms = offerTermsSummary(offer);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: superseded ? 'divider' : 'primary.light',
        borderRadius: 1,
        p: 1.5,
        opacity: superseded ? 0.7 : 1,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {OFFER_TYPE_LABELS[offer.offerType]}
        </Typography>
        {superseded && <Chip size="small" label="Superseded" variant="outlined" />}
        {offer.clientResponse && (
          <Chip
            size="small"
            color={offer.clientResponse === 'accepted' ? 'success' : 'default'}
            label={offer.clientResponse === 'accepted' ? 'Client accepted' : 'Client rejected'}
          />
        )}
      </Stack>

      {terms && (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {terms}
        </Typography>
      )}

      <Typography variant="body2" color="text.secondary">
        Received {formatOfferDate(offer.receivedAt)} — {OFFER_SOURCE_LABELS[offer.source]}
      </Typography>

      {offer.notes && (
        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
          {offer.notes}
        </Typography>
      )}

      <Chip
        size="small"
        sx={{ mt: 1 }}
        color={offer.conveyedAt ? 'success' : 'warning'}
        variant={offer.conveyedAt ? 'filled' : 'outlined'}
        label={offerConveyanceLabel(offer)}
      />
    </Box>
  );
}

export default OfferSummary;
